import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Switch,
  TextField,
  Typography,
  FormControlLabel,
  Paper,
  Divider,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { useSelector } from "react-redux";
import LineSettingApi from "api/LineSettingApi";
import EnvironmentUtils from "utils/EnviromentUtils";

const LineSetting: React.FC = () => {
  const companyId = useSelector((state: any) => state.account.staff.companyId);
  const [lineChannelAccessToken, setLineChannelAccessToken] = useState("");
  const [lineChannelSecret, setLineChannelSecret] = useState("");
  const [lineNotifyEnabled, setLineNotifyEnabled] = useState(false);
  const [lineBotBasicId, setLineBotBasicId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copyTooltip, setCopyTooltip] = useState("コピー");

  const webhookUrl = useMemo(() => {
    if (!companyId) return "";
    // EnvironmentUtils.getApiUrl() の戻り値の例: "https://api.no-tel.com/v1/api"
    // すでに "/api" が含まれているので line/webhook を続ける
    const base = (EnvironmentUtils.getApiUrl?.() || "").replace(/\/$/, "");
    return `${base}/line/webhook/${companyId}`;
  }, [companyId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await LineSettingApi.find(companyId);
        if (result) {
          setLineChannelAccessToken(
            (result as any).lineChannelAccessToken || ""
          );
          setLineChannelSecret((result as any).lineChannelSecret || "");
          setLineNotifyEnabled((result as any).lineNotifyEnabled || false);
          setLineBotBasicId((result as any).lineBotBasicId || "");
        }
      } catch (e) {
        console.error("LINE設定の取得に失敗しました", e);
      }
    };
    if (companyId) fetchData();
  }, [companyId]);

  const handleSave = async () => {
    setLoading(true);
    setSaved(false);
    try {
      await LineSettingApi.update(companyId, {
        lineChannelAccessToken: lineChannelAccessToken || undefined,
        lineChannelSecret: lineChannelSecret || undefined,
        lineNotifyEnabled,
        lineBotBasicId: lineBotBasicId || undefined,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      alert("保存に失敗しました");
    }
    setLoading(false);
  };

  const handleCopyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      setCopyTooltip("コピーしました");
      setTimeout(() => setCopyTooltip("コピー"), 1500);
    } catch (e) {
      setCopyTooltip("コピー失敗");
    }
  };

  return (
    <Box padding={3}>
      <Typography variant="h5" gutterBottom>
        LINE通知設定
      </Typography>
      <Divider style={{ marginBottom: 20 }} />

      <Paper style={{ padding: 24, maxWidth: 720 }}>
        <FormControlLabel
          control={
            <Switch
              checked={lineNotifyEnabled}
              onChange={(e) => setLineNotifyEnabled(e.target.checked)}
              color="primary"
            />
          }
          label="LINE通知を有効にする"
        />

        <Box marginTop={3}>
          <TextField
            fullWidth
            label="チャネルアクセストークン（長期）"
            value={lineChannelAccessToken}
            onChange={(e) => setLineChannelAccessToken(e.target.value)}
            variant="outlined"
            margin="normal"
            type="password"
            placeholder="LINE Developers のチャネルアクセストークンを貼り付け"
            helperText="LINE Developers > 該当チャネル > Messaging API設定 > チャネルアクセストークン（長期）の「発行」ボタンで取得"
          />
        </Box>

        <Box marginTop={1}>
          <TextField
            fullWidth
            label="チャネルシークレット"
            value={lineChannelSecret}
            onChange={(e) => setLineChannelSecret(e.target.value)}
            variant="outlined"
            margin="normal"
            type="password"
            placeholder="LINE Developers のチャネルシークレットを貼り付け"
            helperText="LINE Developers > 該当チャネル > チャネル基本設定 > チャネルシークレットで取得（Webhook 署名検証に使用）"
          />
        </Box>

        <Box marginTop={1}>
          <TextField
            fullWidth
            label="LINE公式アカウント ベーシックID"
            value={lineBotBasicId}
            onChange={(e) => setLineBotBasicId(e.target.value)}
            variant="outlined"
            margin="normal"
            placeholder="@xxxxxxx"
            helperText="LINE Developers > Messaging API設定 > ボット情報 > ボットのベーシックID（@で始まる文字列）"
          />
        </Box>

        <Box marginTop={2}>
          <Typography variant="subtitle2" gutterBottom>
            Webhook URL
          </Typography>
          <Box display="flex" alignItems="center">
            <TextField
              fullWidth
              value={webhookUrl}
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
            />
            <Tooltip title={copyTooltip}>
              <IconButton onClick={handleCopyWebhookUrl} size="small">
                <FileCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="textSecondary">
            この URL を LINE Developers の Webhook URL 欄に貼り付け、Webhook の利用を ON にしてください。
          </Typography>
        </Box>

        <Box marginTop={3} display="flex" alignItems="center">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading}
          >
            {loading ? "保存中..." : "保存"}
          </Button>
          {saved && (
            <Typography style={{ marginLeft: 16, color: "green" }}>
              保存しました
            </Typography>
          )}
        </Box>
      </Paper>

      <Paper style={{ padding: 24, maxWidth: 720, marginTop: 24 }}>
        <Typography variant="h6" gutterBottom>
          設定手順
        </Typography>
        <Typography variant="body2" color="textSecondary" component="div">
          <ol style={{ paddingLeft: 18, lineHeight: 1.8 }}>
            <li>
              <a
                href="https://entry.line.biz/form/entry/unverified"
                target="_blank"
                rel="noopener noreferrer"
              >
                LINE 公式アカウントを作成
              </a>
              （まだお持ちでない場合）
            </li>
            <li>
              LINE Official Account Manager の <strong>設定 &gt; Messaging API</strong> から
              「Messaging APIを利用する」を有効化
            </li>
            <li>
              <a
                href="https://developers.line.biz/console/"
                target="_blank"
                rel="noopener noreferrer"
              >
                LINE Developers コンソール
              </a>
              で該当チャネルを開き、<strong>Messaging API設定</strong> タブから:
              <ul>
                <li>「ボットのベーシックID」(@xxx) を上の欄にコピペ</li>
                <li>「Webhook URL」欄に上のWebhook URLをコピペ → 「Webhookの利用」を ON</li>
                <li>「チャネルアクセストークン（長期）」の「発行」ボタンを押して、表示されたトークンを上の欄にコピペ</li>
              </ul>
            </li>
            <li>
              <strong>チャネル基本設定</strong> タブから「チャネルシークレット」を上の欄にコピペ
            </li>
            <li>
              LINE Official Account Manager の <strong>応答設定</strong> で：
              <ul>
                <li>「応答メッセージ」を <strong>OFF</strong>（オウム返しを止めるため）</li>
                <li>「あいさつメッセージ」は任意（友だち追加時の自動メッセージ）</li>
              </ul>
            </li>
            <li>上の「LINE通知を有効にする」を ON にして保存</li>
            <li>キャスト・ドライバーが各自のアプリで「LINE連携」ボタンから連携</li>
          </ol>
        </Typography>
      </Paper>
    </Box>
  );
};

export default LineSetting;
